import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=user
  const [selectedDate, setSelectedDate] = useState(null);
  const [health, setHealth] = useState(null);

  const refreshHealth = useCallback(async () => {
    try {
      const h = await api.health();
      setHealth(h);
      setSelectedDate((d) => d || h.default_date || h.live_date);
      return h;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("sentinel_token");
    if (!token) {
      setUser(false);
      return;
    }
    api
      .me()
      .then((u) => {
        setUser(u);
        refreshHealth();
      })
      .catch(() => setUser(false));
  }, [refreshHealth]);

  // Any API call answered with PASSWORD_CHANGE_REQUIRED sends the user to the forced password change screen.
  useEffect(() => {
    const onRequired = () => setUser((u) => (u ? { ...u, must_change_password: true } : u));
    window.addEventListener("sentinel:password-change-required", onRequired);
    return () => window.removeEventListener("sentinel:password-change-required", onRequired);
  }, []);

  const login = async (email, password) => {
    const { token, user } = await api.login(email, password);
    localStorage.setItem("sentinel_token", token);
    setUser(user);
    await refreshHealth();
    return user;
  };

  const logout = () => {
    api.logout().catch(() => {});
    localStorage.removeItem("sentinel_token");
    setUser(false);
  };
  /** Signs out on every device, then locally. Throws if the server refuses, so the caller can say so. */
  const logoutAll = async () => {
    await api.logoutAll();
    localStorage.removeItem("sentinel_token");
    setUser(false);
  };

  const roleRank = { viewer: 0, analyst: 1, owner: 2 };
  const can = (min) => !!user && roleRank[user.role] >= roleRank[min];

  return (
    <AuthCtx.Provider
      value={{ user, setUser, login, logout, logoutAll, can, selectedDate, setSelectedDate, health, refreshHealth }}
    >
      {children}
    </AuthCtx.Provider>
  );
}
